import { Builder } from '@builder.io/react'
import React from 'react'

import { HeadingClass, HeadingType } from 'enums'
import Bullet from '../../../images/BulletPoint.svg'
import CustomHeading, { headingTypePropObj, headingClassPropObj } from '../common/heading'

// import './index.css'
import CdnImage from 'components/util/CdnImage'

enum Alignment {
  Center = 'center',
  Left = 'left',
  Right = 'right'
}

interface Props {
  backgroundColor: string
  imageLeft: any
  imageMobile: any
  imageRight: any
  imageLeftAltText: string
  imageRightAltText: string
  headingText: string
  headingTextColor: string
  contentText: string
  headingType: HeadingType
  headingClass: HeadingClass
  textAlignment: Alignment
  bulletPoint: any
  bulletAltText: string
  points: [{ text: string }]
}

const SustainabilitySection = ({
  backgroundColor,
  imageLeft,
  imageMobile,
  imageRight,
  imageLeftAltText,
  imageRightAltText,
  headingText,
  headingTextColor,
  contentText,
  headingType,
  headingClass,
  textAlignment,
  bulletPoint,
  bulletAltText = '',
  points
}: Props) => {
  const getAlignmentClass = () => {
    if (textAlignment === Alignment.Right) {
      return 'text-right'
    }
    if (textAlignment === Alignment.Center) {
      return 'text-center'
    }
    return 'text-left'
  }

  const renderLeftImage = () => {
    return (
      <CdnImage
        className="max-h-[300px] w-full object-contain lg:pt-4"
        url={imageLeft}
        alt={imageLeftAltText}
        steps={[705]}
      />
    )
  }

  const renderImageMobile = () => {
    return <CdnImage className="md:h-44 w-full object-contain" url={imageMobile} alt={imageLeftAltText} steps={[705]} />
  }

  const renderRightImage = () => {
    return (
      <div className="flex h-full w-full items-end justify-center">
        <CdnImage className="max-h-60 w-full object-contain" url={imageRight} alt={imageRightAltText} steps={[705]} />
      </div>
    )
  }

  const getContent = () => {
    return (
      <>
        <div className="relative w-full px-4 sm:px-6 lg:mx-auto lg:grid lg:max-w-7xl lg:grid-cols-4 lg:px-8 lg:gap-8 justify-center items-center">
          {/* Left Image – Desktop */}
          <div className="hidden lg:flex lg:col-span-1 items-end">{renderLeftImage()}</div>

          {/* Middle Section */}
          <div className="col-span-4 lg:col-span-2 mt-8 lg:mt-0">
            <div className="word-break-fix mx-auto max-w-prose text-base">
              <CustomHeading
                classList="line-height-fix w-full h-full tracking-tight text-gray-900 text-center lg:text-left font-medium"
                headingClass={headingClass}
                headingType={headingType}
                color={headingTextColor}
              >
                {headingText}
              </CustomHeading>
              <div className="prose prose-indigo mt-5 text-gray-500">
                <p
                  className={`enable-list-styles h-full w-full text-base ${getAlignmentClass()}`}
                  dangerouslySetInnerHTML={{ __html: contentText }}
                ></p>
              </div>
              <div className="max-h-ful mt-4 mb-4 flex max-w-full flex-col gap-y-3">
                {points?.map((point: any, i: number) => (
                  <div key={i} className="flex items-center">
                    <img
                      src={bulletPoint || Bullet}
                      className="mr-2 h-8 w-8"
                      alt={bulletAltText || 'Different Dog logo'}
                    />
                    <p className="font-normal text-black" dangerouslySetInnerHTML={{ __html: point.text }}></p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Image – Desktop */}
          <div className="hidden lg:block h-auto lg:col-span-1 mt-8">{renderRightImage()}</div>
        </div>

        {/* Mobile Image Row */}
        <div className="mt-6 flex w-full lg:hidden justify-center mx-auto">
          <div className="w-full flex justify-center items-end">{renderImageMobile()}</div>
        </div>
      </>
    )
  }

  return (
    <>
      <div
        className="relative flex flex-col bg-auto bg-no-repeat lg:bg-100%"
        style={{ backgroundColor: backgroundColor }}
      >
        {getContent()}
      </div>
    </>
  )
}

export default SustainabilitySection

Builder.registerComponent(SustainabilitySection, {
  name: 'SustainabilitySection',
  inputs: [
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'white'
    },
    {
      name: 'imageLeft',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'imageMobile',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'imageRight',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },

    {
      name: 'imageLeftAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'imageRightAltText',
      type: 'text',
      defaultValue: ''
    },

    {
      ...headingTypePropObj
    },
    {
      ...headingClassPropObj
    },
    {
      name: 'headingText',
      type: 'text',
      defaultValue: 'Javascript for Beginners'
    },
    {
      name: 'headingTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'contentText',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'textAlignment',
      type: 'string',
      enum: ['left', 'center', 'right'],
      defaultValue: 'left'
    },
    {
      name: 'bulletPoint',
      type: 'file',
      defaultValue: Bullet
    },
    {
      name: 'bulletAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'points',
      type: 'list',
      subFields: [{ name: 'text', type: 'richText' }],
      defaultValue: []
    }
  ]
})
